import { FC } from 'react';
import * as React from 'react';
import { graphql, createFragmentContainer, useRelayEnvironment } from 'react-relay';

import CustomsMessaging from 'dibs-customs-messaging/exports/CustomsMessaging';
import PdpSharedShippingInfoWrapper from '../PdpSharedShippingInfo/PdpSharedShippingInfoWrapper';
import { PdpSharedDetailShipsFrom } from './PdpSharedDetailShipsFrom';

import { PdpSharedDetailShipping_viewer$data as ViewerType } from './__generated__/PdpSharedDetailShipping_viewer.graphql';
import { PdpSharedDetailShipping_item$data as ItemType } from './__generated__/PdpSharedDetailShipping_item.graphql';
import { PdpSharedDetailShipping_user$data as UserType } from './__generated__/PdpSharedDetailShipping_user.graphql';

type Props = {
    viewer: ViewerType;
    item: ItemType;
    user: UserType;
    itemId?: string;
    userId?: string;
    currency: string;
    displayInline: boolean;
    preLinkQuoteDetails: React.ReactNode;
    postLinkQuoteDetails: React.ReactNode;
    isClient: boolean;
    postLinkNoQuoteDetails: React.ReactNode;
    itemQuantity?: number;
};

export const PdpSharedDetailsShippingComponent: FC<Props> = ({
    viewer,
    item,
    currency,
    user,
    displayInline,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    isClient,
    itemQuantity,
}) => {
    const environment = useRelayEnvironment();

    const userId = user?.serviceId;

    const { serviceId: itemId, isSold, isUnpurchasable, isAuctionEnabled, isMultiSku, attributes } =
        item || {};
    const leadTime = attributes?.leadTime?.[0]?.option?.urlLabel;

    return (
        <>
            <PdpSharedShippingInfoWrapper
                user={user}
                item={item}
                currency={currency}
                leadTime={leadTime || ''}
                renderTooltip
                isClient={isClient}
                displayInline={displayInline}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                itemQuantity={itemQuantity}
                //calculate lower shipping info only if item is regular PDP
                calculateLowerShippingInfo={
                    !!viewer.favorableShipping &&
                    !isSold &&
                    !isUnpurchasable &&
                    !isAuctionEnabled &&
                    !isMultiSku
                }
            >
                <PdpSharedDetailShipsFrom item={item} />
            </PdpSharedShippingInfoWrapper>
            <CustomsMessaging
                environment={environment}
                userId={userId}
                itemId={itemId}
                currency={currency}
            />
        </>
    );
};

export const PdpSharedDetailShipping = createFragmentContainer(PdpSharedDetailsShippingComponent, {
    viewer: graphql`
        fragment PdpSharedDetailShipping_viewer on Viewer {
            favorableShipping: featureFlag(feature: "favorableShipping")
        }
    `,
    user: graphql`
        fragment PdpSharedDetailShipping_user on User {
            serviceId
            ...PdpSharedShippingInfoWrapper_user
        }
    `,
    item: graphql`
        fragment PdpSharedDetailShipping_item on Item {
            ...PdpSharedShippingInfoWrapper_item
            ...PdpSharedDetailShipsFrom_item
            serviceId
            isSold
            isUnpurchasable
            isAuctionEnabled
            isMultiSku
            attributes {
                leadTime {
                    option {
                        urlLabel
                    }
                }
            }
        }
    `,
});
